import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, UsersIcon, MapPinIcon, ZapIcon, SearchIcon, LayoutIcon, CodeIcon, MousePointerClickIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
import { AlkmaarSkyline } from '../components/AlkmaarSkyline';
export function WebsiteLatenMakenAlkmaarPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken Alkmaar | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website laten maken in Alkmaar? Noord Digital bouwt professionele, conversiegerichte websites voor ondernemers in Alkmaar en omgeving. Vraag een vrijblijvend gesprek aan.';
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website in Alkmaar?',
    a: 'De kosten voor een website in Alkmaar variëren afhankelijk van je wensen en het type website. Een eenvoudige bedrijfswebsite begint vanaf een paar honderd euro, terwijl maatwerk websites meer kosten. Wij geven altijd vooraf een transparante prijsopgave.'
  }, {
    q: 'Hoe lang duurt het om een website te maken?',
    a: 'Een standaard bedrijfswebsite is meestal binnen 2 tot 4 weken live. Voor complexere projecten plannen we samen een realistische tijdlijn. Wij houden je gedurende het hele proces op de hoogte.'
  }, {
    q: 'Kan ik mijn website zelf aanpassen?',
    a: 'Ja, wij bouwen onze websites met WordPress, zodat je zelf eenvoudig teksten en afbeeldingen kunt aanpassen. Na oplevering krijg je een uitgebreide uitleg en handleiding.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2250">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2251">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2252">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2253" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2254" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2255" />
        </div>

        {/* Alkmaar Skyline */}
        <div className="absolute bottom-0 left-0 right-0 opacity-[0.08] pointer-events-none" data-id="element-2256">
          <AlkmaarSkyline className="w-full h-auto" color="#ffffff" data-id="element-2257" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2258">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2259">
            Website laten maken in Alkmaar
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2260">
            Professionele websites voor ondernemers in Alkmaar en omgeving. Wij
            zijn jouw lokale webdesigner met persoonlijke aanpak.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2261">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2262">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-2263" />
            </a>
          </div>
        </div>
      </section>

      {/* Voor ondernemers in Alkmaar */}
      <section className="py-24 bg-surface-muted" data-id="element-2264">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2265">
          <div className="text-center mb-12" data-id="element-2266">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2267">
              Voor ondernemers in Alkmaar
            </h2>
          </div>
          <div className="max-w-3xl mx-auto text-center" data-id="element-2268">
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2269">
              Als webdesign bureau in Alkmaar begrijpen wij de lokale markt. Wij
              helpen ondernemers in Alkmaar, Heerhugowaard, Bergen, Heiloo en
              omgeving met professionele websites die klanten opleveren. Of je
              nu een nieuwe website nodig hebt of je huidige site wilt
              verbeteren — wij staan voor je klaar.
            </p>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-2270">
              Of je nu een nieuwe website wilt laten maken, beter gevonden wilt
              worden in Google, of je huidige website wilt vernieuwen en
              verbeteren. Wij staan voor je klaar.
            </p>
          </div>
        </div>
      </section>

      {/* Waarom een lokale webdesigner */}
      <section className="py-24 bg-white" data-id="element-2271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2272">
          <div className="text-center mb-16" data-id="element-2273">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2274">
              Waarom een lokale webdesigner
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2275">
              Kies voor een webdesign bureau in Alkmaar dat dichtbij is en met
              je meedenkt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2276">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2277">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2278">
                <UsersIcon className="w-6 h-6 text-accent" data-id="element-2279" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2280">
                Persoonlijk contact
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2281">
                Direct contact met je vaste webdesigner. Geen callcenter, maar
                een persoonlijke aanpak.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2282">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2283">
                <MapPinIcon className="w-6 h-6 text-accent" data-id="element-2284" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2285">
                Lokale kennis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2286">
                Wij kennen de Alkmaarse markt en weten wat werkt voor lokale
                ondernemers.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2287">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2288">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-2289" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2290">
                Snelle communicatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2291">
                Korte lijnen en snelle reactietijden. Binnen 24 uur antwoord op
                je vraag.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2292">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2293">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2294" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2295">
                Betere SEO
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2296">
                Geoptimaliseerd voor lokale zoekresultaten in Alkmaar en
                Noord-Holland.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat we bouwen */}
      <section className="py-24 bg-surface-muted" data-id="element-2297">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2298">
          <div className="text-center mb-16" data-id="element-2299">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2300">
              Wat we bouwen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2301">
              Van simpele bedrijfswebsites tot uitgebreide WordPress
              oplossingen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2302">
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2303">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2304">
                <LayoutIcon className="w-6 h-6 text-navy-700" data-id="element-2305" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2306">
                Bedrijfswebsites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2307">
                Een professioneel online visitekaartje dat jouw diensten en
                expertise presenteert aan klanten in Alkmaar.
              </p>
            </div>
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2308">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2309">
                <CodeIcon className="w-6 h-6 text-navy-700" data-id="element-2310" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2311">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2312">
                Flexibele WordPress websites die je zelf eenvoudig kunt beheren
                en aanpassen.
              </p>
            </div>
            <div className="bg-white border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2313">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center mb-6" data-id="element-2314">
                <MousePointerClickIcon className="w-6 h-6 text-navy-700" data-id="element-2315" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2316">
                Conversie websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2317">
                Websites gebouwd om bezoekers om te zetten in klanten, met
                slimme call-to-actions en formulieren.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ons proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2318">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2319" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2320">
          <div className="text-center mb-16" data-id="element-2321">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2322">
              Ons proces
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2323">
              In 5 heldere stappen naar jouw nieuwe website.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2324">
            {['Kennismaking', 'Strategie', 'Ontwerp', 'Ontwikkeling', 'Livegang'].map((step, i) => <div key={step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-2325">
                <span className="absolute top-2 right-4 text-4xl font-bold text-white/[0.05]" data-id="element-2326">
                  {i + 1}
                </span>
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-2327">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold" data-id="element-2328">{step}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2329">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2330">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2331">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2332">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2333">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2334">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2335">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2336">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2337">
              Hosting & onderhoud
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2338">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2339">
          <div className="text-center mb-16" data-id="element-2340">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2341">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2342">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2343">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2344">
                  <span className="font-bold text-navy-900" data-id="element-2345">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2346" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2347" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2348">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2349" />
    </main>;
}